import { Box, IconButton, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tooltip } from "@mui/material";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import SwitchToggle from "../../components/SwitchToggle";
import { useGetSubAdminsQuery, useUpdateSubAdminStatusMutation, useDeleteSubAdminMutation } from "../../service/features/api";
import moment from "moment";
import Pagination from "../../components/Panigation";
import { toast } from "react-toastify";
import DeleteConfirmModal from "../../Modals/DeleteConfirmModal";

const SubadminList = ({ search }: { search: string }) => {
    const navigate = useNavigate();
    const [page, setPage] = useState(1);
    const limit = 10;

    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [selectedId, setSelectedId] = useState<string | null>(null);

    const { data: subAdminsData, isLoading } = useGetSubAdminsQuery({
        search,
        page,
        limit
    });

    const [updateStatus] = useUpdateSubAdminStatusMutation();
    const [deleteSubAdmin, { isLoading: isDeleting }] = useDeleteSubAdminMutation();

    const subAdmins = subAdminsData?.data?.data || [];
    const totalCount = subAdminsData?.data?.paginator?.itemCount || 0;
    const totalPages = subAdminsData?.data?.paginator?.pageCount || 1;

    const handleStatusChange = async (id: string, isBlocked: boolean) => {
        try {
            const res = await updateStatus({ id, isBlocked }).unwrap();
            toast.success(res?.message || "Status updated successfully");
        } catch (error: any) {
            toast.error(error?.data?.message || "Failed to update status");
        }
    };

    const handleDeleteClick = (id: string) => {
        setSelectedId(id);
        setDeleteModalOpen(true);
    };

    const handleConfirmDelete = async () => {
        if (!selectedId) return;
        try {
            await deleteSubAdmin(selectedId).unwrap();
            toast.success("Account deleted successfully");
            setDeleteModalOpen(false);
            setSelectedId(null);
        } catch (error) {
            toast.error("Failed to delete account");
        }
    };

    const columns = [
        { id: "name", label: "Name" },
        { id: "role", label: "Role" },
        { id: "date", label: "Created Date" },
        { id: "status", label: "Disable/Enable" },
        { id: "lastLogin", label: "Last Login" },
        { id: "action", label: "Action" }
    ];

    if (isLoading) return <div className="loading_state">Loading...</div>;

    return (
        <>
            <TableContainer className="table_container">
                <Table>
                    <TableHead>
                        <TableRow hover>
                            {columns.map((column) => (
                                <TableCell key={column.id}>{column.label}</TableCell>
                            ))}
                        </TableRow>
                    </TableHead>

                    <TableBody>
                        {subAdmins.map((row: any) => (
                            <TableRow hover key={row._id}>
                                <TableCell>
                                    <div
                                        onClick={() => navigate("/manage-subadmin/detail", { state: { id: row._id } })}
                                        className="user_block click_action"
                                    >
                                        <figure>
                                            <img src={row.profile || "/static/images/user_placeholder.png"} alt="User" />
                                        </figure>
                                        <p><b>{row.fullName}</b> {row.email}</p>
                                    </div>
                                </TableCell>
                                <TableCell>
                                    <div
                                        onClick={() => navigate("/manage-subadmin/roles/detail", { state: { id: row.permission?._id } })}
                                        className="user_block click_action"
                                    >
                                        <p>
                                            <b>{row.permission?.name}</b>
                                        </p>
                                    </div>
                                </TableCell>
                                <TableCell>{moment(row.createdAt).format("DD MMMM YYYY")}</TableCell>

                                <TableCell>
                                    <SwitchToggle
                                        checked={!row.isBlocked}
                                        onChange={(e) => handleStatusChange(row._id, !e.target.checked)}
                                    />
                                </TableCell>

                                <TableCell>{row.lastLogin ? moment(row.lastLogin).format("DD MMMM YYYY, hh:mm A") : "N/A"}</TableCell>

                                <TableCell>
                                    <div className="table_actions v2">
                                        <Tooltip title="View" arrow>
                                            <Box>
                                                <IconButton onClick={() => navigate("/manage-subadmin/detail", { state: { id: row._id } })}>
                                                    <img src="/static/images/eye_icon.svg" alt="View" />
                                                </IconButton>
                                            </Box>
                                        </Tooltip>
                                        <Tooltip title="Edit" arrow>
                                            <Box>
                                                <IconButton onClick={() => navigate("/manage-subadmin/add", { state: { id: row._id } })}>
                                                    <img src="/static/images/edit2_icon.svg" alt="Edit" />
                                                </IconButton>
                                            </Box>
                                        </Tooltip>
                                        <Tooltip title="Delete" arrow>
                                            <Box>
                                                <IconButton onClick={() => handleDeleteClick(row._id)}>
                                                    <img src="/static/images/trash2_icon.svg" alt="Delete" />
                                                </IconButton>
                                            </Box>
                                        </Tooltip>
                                    </div>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
            <Pagination
                module={subAdmins}
                page={page}
                totalPages={totalPages}
                totalCount={totalCount}
                setPage={setPage}
                onPageChange={(p) => setPage(p)}
            />

            <DeleteConfirmModal
                open={deleteModalOpen}
                onClose={() => setDeleteModalOpen(false)}
                onConfirm={handleConfirmDelete}
                title="Delete Sub-Admin"
                message="Are you sure you want to delete this sub-admin account?"
                isLoading={isDeleting}
            />
        </>
    );
};

export default SubadminList;
